import React, { useRef } from 'react';
import { Form, Radio, Space, Input, Button, message } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useRouter } from 'next/router';
import styles from './index.module.scss';
import NextImage from '@/components/NextImage';
import EFVideo from '@/components/UI/EFVideo';
import { handleDynamicPath } from '@/utils/handleDynamicPath';
import { useGlobal } from '@/utils/useGlobal';
import { getQuestionnaireByScene, submitDelta3Questionnaire } from '@/service';
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ProtablePowerStationQuestionnaire: React.FC = () => {
    const { useState, useEffect, useLang, gtm } = useGlobal();
    const { t } = useLang('questionnaire');
    const router = useRouter();
    const { locale } = router;
    const [form] = Form.useForm();
    const [isSelect, setIsSelect] = useState(false);
    const [question, setQuestion] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const optionClick = useRef<number>(-1);
    message.config({ top: 120 });

    const handleRadioChange = (e: RadioChangeEvent) => {
        if (e.target.value) {
            setIsSelect(true);
        } else {
            setIsSelect(false);
        }
    };

    const queryQuestionnaireByScene = async () => {
        const params = {
            adapterScene: 'Delta3',
        };

        const res = await getQuestionnaireByScene(params, locale as string);

        if (res.code === '0' && res.data?.length) {
            setQuestion({
                ...res.data[0],
                choiceOptions: res.data[0].choiceOptions ? JSON.parse(res.data[0].choiceOptions) : {},
            });
        }
    };

    const handleSubmit = () => {
        setIsLoading(true);
        form.validateFields()
            .then(async (valid) => {
                if (valid) {
                    if (!valid.answer) {
                        return;
                    }
                    const answer = valid.answer?.replace(/<[^>]*>/g, '');
                    const params = {
                        questionType: 'MULTIPLE_CHOICE',
                        questionTitle: question.questionTitle,
                        questionId: question.questionId,
                        questionDesc: question.questionDesc,
                        answer,
                        email: valid.email?.trim(),
                        name: valid.name?.trim(),
                    };

                    const res = await submitDelta3Questionnaire(params, locale as string);

                    if (res.code === '0') {
                        gtm.push({
                            event: 'uaEvent',
                            event_name: 'questionnaire_submit',
                            submit_status: 'success',
                            title_name: 'Delta3 Questionnaire',
                        });
                        message.success(res.message);
                        form.resetFields();
                        setIsSelect(false);
                        optionClick.current = -1;
                    } else {
                        gtm.push({
                            event: 'uaEvent',
                            event_name: 'questionnaire_submit',
                            submit_status: 'fail',
                            title_name: 'Delta3 Questionnaire',
                        });
                    }
                }
                setIsLoading(false);
            })
            .catch((err) => {
                setIsLoading(false);
            });
    };

    const onRadioClick = (e: any, index: number, answer: any) => {
        optionClick.current = index + 1;
        gtm.push({
            event: 'uaEvent',
            event_name: 'option_select',
            button_name: answer,
            title_name: 'Delta3 Questionnaire',
            position: optionClick.current,
        });
    };

    useEffect(() => {
        queryQuestionnaireByScene();
    }, []);

    return (
        <div className={styles.StationQuestionnaire}>
            <div className="introduction">
                <h5>{t('title')}</h5>
                <div className="desc">{t('desc')}</div>
                <div className="question-kv">
                    <NextImage src={require('@publicLocal/protable-power-station-questionnaire/questionnaire_kv.jpg')} alt="ecoflow"></NextImage>
                </div>
            </div>
            <div className="question-form">
                <Form colon={false} form={form} onFinish={handleSubmit}>
                    <div className="question-item">
                        <div className="question-desc">{question.questionTitle}</div>
                        <Form.Item name="answer">
                            <Radio.Group onChange={handleRadioChange}>
                                <Space direction="vertical">
                                    {Object.keys(question.choiceOptions || {}).map((item: any, index) => (
                                        <Radio
                                            value={question.choiceOptions[item]}
                                            key={item}
                                            onClick={(e) => onRadioClick(e, index, question.choiceOptions[item])}
                                        >
                                            <div className="option">
                                                <div className="rideo">
                                                    <EFVideo
                                                        src={handleDynamicPath(`/video/questionnaire/plan_${index + 1}.mp4`)}
                                                        // sources={sources}
                                                        showReplay={false}
                                                        id={`questionnaireOption${index + 1}`}
                                                        endFrame={0}
                                                        muted
                                                        autoPlay={true}
                                                        loop={true}
                                                        preload="auto"
                                                        width="100%"
                                                        height="100%"
                                                        playsInline
                                                        aria-label={`plan${index + 1}.mp4`}
                                                    ></EFVideo>
                                                </div>
                                                <div className="content" dangerouslySetInnerHTML={{ __html: question.choiceOptions[item] }}>
                                                    {/* <p className="option-desc">{item.desc}</p>
                                                    <span className="price">{item.price}</span> */}
                                                </div>
                                            </div>
                                        </Radio>
                                    ))}
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                    </div>
                    {isSelect && (
                        <div className="question-item">
                            <div className="question-desc">{t('question2_desc')}</div>
                            <Form.Item name="name" label={t('name')}>
                                <Input placeholder={t('input_here')} maxLength={100} />
                            </Form.Item>
                            <Form.Item
                                name="email"
                                label={t('email')}
                                rules={[
                                    {
                                        validator: (_: any, value: string) => {
                                            if (!value) {
                                                return Promise.resolve();
                                            }
                                            if (!emailRegex.test(value)) {
                                                return Promise.reject('please enter the vaild email');
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input placeholder={t('input_here')} maxLength={100} />
                            </Form.Item>
                        </div>
                    )}
                    <Button className="submit-btn" type="primary" disabled={!isSelect} htmlType="submit" loading={isLoading} aria-label={question.questionDesc}>
                        {t('submit')}
                    </Button>
                </Form>
            </div>
        </div>
    );
};

export default ProtablePowerStationQuestionnaire;
